import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'

import { api } from '@/app/services/api'
import { useGetOrCreateUserContextMutation } from '@/app/services/user'
import { selectCurrentUser, setAppIsLoading, setCurrentUser } from '@/app/slices/appSlice'
import { LOADING_STEPS, Paths } from '@/common/utils'
import { handleAuthenticationError } from '@/common/utils/AuthErrors'
import { determineRedirectPath } from '@/common/utils/subscriptionUtils'

import { useBroadcastChannel } from './useBroadcastChannel'
import { useIntercomMessenger } from './useIntercomMessenger'
import useLoadingContext from './useLoadingContext'
import { useManufacturingDataLoader } from './useManufacturingDataLoader'
import { useOrganisationLoader } from './useOrganisationLoader'
import { useToolBoxTreatments } from './useToolBoxTreatments'

export const useUserContextSwitcher = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const appInsights = useAppInsightsContext()

    const currentUser = useSelector(selectCurrentUser)

    const { showIntercom } = useToolBoxTreatments()
    const { loadOrganisationContext } = useOrganisationLoader()
    const { loadManufacturingData } = useManufacturingDataLoader()
    const { bootIntercomMessenger, shutdown } = useIntercomMessenger()
    const { resetLoadingFromStep } = useLoadingContext()

    const [getOrCreateUserContext] = useGetOrCreateUserContextMutation()

    const { postMessage } = useBroadcastChannel('orgChangeChannel', (message) => {
        try {
            if (message === 'ORG_CHANGED') {
                navigate(Paths.DASHBOARD_PATHNAME)
                window.location.reload()
            }
        } catch (error) {
            console.error('Failed to process message:', error)
        }
    })

    const trackEvent = ({ data, eventName }) => appInsights.trackEvent({ name: eventName, properties: data })

    const setSelectedOrganisation = async (newSelectedOrganisationId) => {
        resetLoadingFromStep(LOADING_STEPS.ORGANISATION)
        dispatch(setAppIsLoading(true))
        dispatch(api.util.resetApiState())
        shutdown() //Shutdown Intercom to avoid cross-org data leakage

        try {
            // Step 1: Update user context with new organization ID
            console.debug('[UserContextSwitcher] Updating user context for organization:', newSelectedOrganisationId)
            const userContext = await getOrCreateUserContext({
                user: { ...currentUser, lastAccessedOrganisationId: newSelectedOrganisationId },
            }).unwrap()

            const appUser = userContext.user
            const selectedOrganisationId = userContext.selectedOrganisationId

            // Step 2: Use centralized organization loading
            const { organisation } = await loadOrganisationContext(appUser, selectedOrganisationId, userContext)

            // Step 3: Load Manufacturing data for the organization (fire-and-forget)
            loadManufacturingData(selectedOrganisationId)

            // Step 4: Update current user in Redux
            dispatch(setCurrentUser(appUser))

            // Step 6: Handle redirect logic
            const redirectTo = determineRedirectPath(organisation) || Paths.DASHBOARD_PATHNAME

            trackEvent({
                eventName: `User redirected to ${redirectTo}`,
                data: { currentUser: appUser, organisation },
            })

            navigate(redirectTo)
            postMessage('ORG_CHANGED')
            setTimeout(() => {
                dispatch(setAppIsLoading(false))
            }, 300)

            //Step 5: Initialize Intercom
            if (showIntercom) {
                bootIntercomMessenger(organisation, userContext)
            }
            console.debug('[UserContextSwitcher] Organization switch completed successfully')

            return organisation
        } catch (error) {
            console.error('[UserContextSwitcher] Organization switch failed:', error)
            dispatch(setAppIsLoading(false))

            // Use typed error handling
            handleAuthenticationError(error, null, null, (authError) => {
                trackEvent({
                    eventName: 'Organization switch failed',
                    data: {
                        errorCode: authError.code,
                        errorMessage: authError.message,
                        newOrganisationId: newSelectedOrganisationId,
                    },
                })
            })

            navigate(Paths.ERROR_PATHNAME)
        }
    }

    return {
        setSelectedOrganisation,
    }
}
